
/**
 * DimensionTooltip - Floating tooltip that shows live dimensions during drawing
 *
 * Features:
 * - Follows mouse cursor with offset
 * - Shows width × height in cm for rectangles
 * - Shows radius/diameter for circles/ellipses
 * - Shows length and angle for lines
 * - Shows "Quadrat" or "Kreis" indicator when Shift is pressed
 * - Shows instruction text for Bezier tool (point count, minimum requirement)
 * - Emerald border matching design system
 *
 * @param {object} position - Mouse position {x, y}
 * @param {object} dimensions - Shape dimensions {width, height} OR {radiusX, radiusY} OR {length, angle} in pixels/degrees
 * @param {string} type - Shape type: 'rectangle', 'ellipse', 'line', or 'instruction'
 * @param {boolean} isSquare - Whether Shift is pressed (square/circle mode)
 * @param {boolean} visible - Whether tooltip should be shown
 * @param {number} pixelsPerCm - Conversion factor (default: 1 pixel = 1 cm)
 * @param {string} instructionText - Instruction text to display (for type='instruction')
 */
const DimensionTooltip = ({
  position,
  dimensions,
  type = 'rectangle',
  isSquare = false,
  visible = false,
  pixelsPerCm = 1,
  instructionText = '',
}) => {
  if (!visible || !position) {
    return null;
  }

  // For instruction mode, we don't need dimensions
  if (type === 'instruction' && !instructionText) {
    return null;
  }

  // For other modes, we need dimensions
  if (type !== 'instruction' && !dimensions) {
    return null;
  }

  // Convert pixels to cm based on type
  let displayContent;
  let areaSqm;

  if (type === 'instruction') {
    // Instruction mode - show instruction text (for Bezier tool)
    displayContent = (
      <div className="text-sm font-semibold text-gray-900 text-center">
        {instructionText}
      </div>
    );
  } else if (type === 'line') {
    // Line mode - show length and angle
    const lengthM = dimensions.length / 100; // Convert cm to meters
    const angle = dimensions.angle;

    displayContent = (
      <div className="text-sm font-semibold text-gray-900 text-center">
        {lengthM.toFixed(2)} m • {angle}°
      </div>
    );
  } else if (type === 'ellipse') {
    // Ellipse/Circle mode
    const radiusXCm = Math.round(dimensions.radiusX / pixelsPerCm);
    const radiusYCm = Math.round(dimensions.radiusY / pixelsPerCm);
    const widthCm = radiusXCm * 2;
    const heightCm = radiusYCm * 2;

    // Calculate area (π × radiusX × radiusY)
    const areaCm2 = Math.PI * radiusXCm * radiusYCm;
    areaSqm = areaCm2 / 10000;

    if (isSquare || Math.abs(radiusXCm - radiusYCm) < 1) {
      // Circle mode (equal radii)
      displayContent = (
        <>
          <div className="text-sm font-semibold text-gray-900 text-center">
            Ø {widthCm} cm
          </div>
          <div className="text-xs text-gray-500 text-center mt-1">
            Radius: {radiusXCm} cm
          </div>
        </>
      );
    } else {
      // Ellipse mode (different radii)
      displayContent = (
        <>
          <div className="text-sm font-semibold text-gray-900 text-center">
            {widthCm} × {heightCm} cm
          </div>
          <div className="text-xs text-gray-500 text-center mt-1">
            Radius: {radiusXCm} / {radiusYCm} cm
          </div>
        </>
      );
    }
  } else {
    // Rectangle mode
    const widthCm = Math.round(dimensions.width / pixelsPerCm);
    const heightCm = Math.round(dimensions.height / pixelsPerCm);
    areaSqm = (widthCm * heightCm) / 10000;

    displayContent = (
      <div className="text-sm font-semibold text-gray-900 text-center">
        {widthCm} × {heightCm} cm
      </div>
    );
  }

  // Position tooltip near mouse cursor with offset
  const tooltipStyle = {
    position: 'fixed',
    left: `${position.x + 15}px`,
    top: `${position.y + 15}px`,
    zIndex: 9999,
    pointerEvents: 'none', // Don't interfere with mouse events
  };

  return (
    <div style={tooltipStyle}>
      <div
        className="bg-white/95 backdrop-blur-sm border-2 border-emerald-500 rounded shadow-lg px-3 py-2"
        style={{
          minWidth: '100px',
        }}
      >
        {/* Dimensions (different for rectangle vs ellipse) */}
        {displayContent}

        {/* Square/Circle indicator */}
        {isSquare && (
          <div className="text-xs text-emerald-600 text-center mt-1 font-medium">
            {type === 'ellipse' ? '○ Kreis' : '□ Quadrat'}
          </div>
        )}

        {/* Area in m² */}
        {areaSqm > 0 && (
          <div className="text-xs text-gray-500 text-center mt-1">
            {areaSqm.toFixed(2)} m²
          </div>
        )}
      </div>
    </div>
  );
};

export default DimensionTooltip;
